import { motion } from 'framer-motion'
import { Tag, Buildings, Storefront, FirstAid, CheckCircle } from '@phosphor-icons/react'
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { formatEther, Abi, formatUnits, parseUnits } from 'viem'
import { useAccount, useNetwork, usePublicClient, useWalletClient, useBalance } from 'wagmi'
import { ConnectButton } from '@rainbow-me/rainbowkit'
import { ADDRESSES, CONFIG, CHAIN_ID } from '../config'
import { formatTokenAmount } from '../utils/formatting'
import { useTrinityMaster } from '../contracts/trinity'
import { Link } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { useEntityDetails } from '../hooks/useEntityDetails'
import { useAllowance } from '../hooks/useAllowance'
import { usePrepareContractWrite, useContractWrite, useWaitForTransaction, useContractRead } from 'wagmi'
import { EntitySelectionModal } from './EntitySelectionModal'

// Types for marketplace items
interface ForSaleItem {
  type: 'entity' | 'product' | 'item';
  address: `0x${string}`;
  name: string;
  description: string;
  price: bigint;
  owner: `0x${string}`;
  entityAddress?: `0x${string}`;
  productAddress?: `0x${string}`;
  id?: number;
}

// Map entity types to icons
const ENTITY_TYPE_ICONS = {
  'Bank': Buildings,
  'Store': Storefront,
  'Hospital': FirstAid,
  'Unknown': Tag,
} as const;

type EntityType = keyof typeof ENTITY_TYPE_ICONS;

// Success dialog component
function SuccessDialog({ isOpen, onClose, item }: { isOpen: boolean; onClose: () => void; item: ForSaleItem | null }) {
  if (!isOpen || !item) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-gradient-to-br from-amber-500/20 via-yellow-500/20 to-orange-500/20 rounded-2xl p-8 max-w-md w-full mx-4"
      >
        <div className="text-center">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} className="text-green-500" weight="fill" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">Purchase Successful!</h3>
          <p className="text-white/60 mb-6">
            You have successfully purchased {item.name}
          </p>
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all"
          >
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Component for marketplace items
function MarketplaceItem({ 
  item,
  onPurchase,
  onApprove,
  isOwnedByUser,
  canPurchase,
  needsApproval
}: { 
  item: ForSaleItem;
  onPurchase: () => void;
  onApprove: () => void;
  isOwnedByUser: boolean;
  canPurchase: boolean;
  needsApproval: boolean;
}) {
  const Icon = item.type === 'entity' ? Buildings : Storefront;

  const handleClick = () => {
    console.log('Button clicked:', { 
      item, 
      isOwnedByUser, 
      canPurchase, 
      needsApproval,
      type: item.type 
    });
    
    if (needsApproval) {
      onApprove();
    } else {
      onPurchase();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={`relative bg-gradient-to-br from-amber-500/20 via-yellow-500/20 to-orange-500/20 rounded-xl p-4 ${
        isOwnedByUser ? 'opacity-50' : ''
      }`}
    >
      <div className="flex items-center space-x-4">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-lg flex items-center justify-center">
            <Icon size={24} className="text-white/60" />
          </div>
        </div>
        <div className="flex-grow">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-white">{item.name}</h3>
              <p className="text-white/60 text-sm">{item.description}</p>
            </div>
            <div className="text-right">
              <div className="text-white/60 text-sm mb-1">Price</div>
              <div className="text-white font-bold">{formatEther(item.price)} TRINITY</div>
            </div>
          </div>
        </div>
        <div className="flex-shrink-0">
          {isOwnedByUser ? (
            <button
              disabled
              className="px-4 py-2 bg-gray-500/50 text-white/50 rounded-lg cursor-not-allowed"
            >
              Owned
            </button>
          ) : (
            <button
              onClick={handleClick}
              disabled={!canPurchase && !needsApproval}
              className={`px-4 py-2 rounded-lg ${
                (canPurchase || needsApproval)
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600'
                  : 'bg-gray-500/50 text-white/50 cursor-not-allowed'
              }`}
            >
              {needsApproval ? 'Approve' : 'Purchase'}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// Separate component for handling approvals and purchases
function ApprovalHandler({ 
  item, 
  selectedEntity,
  onApprovalReady, 
  onApprovalError,
  onPurchaseComplete,
  onPurchaseError
}: { 
  item: ForSaleItem | null;
  selectedEntity: `0x${string}` | null;
  onApprovalReady: (approve: () => Promise<void>) => void;
  onApprovalError: (error: Error) => void;
  onPurchaseComplete: () => void;
  onPurchaseError: (error: Error) => void;
}) {
  const [isPurchaseInProgress, setIsPurchaseInProgress] = useState(false);

  // Get the correct approval address based on item type
  const getApprovalAddress = () => {
    if (!item) return null;
    switch (item.type) {
      case 'entity':
        return item.address;
      case 'product':
        return item.address;
      case 'item':
        return item.productAddress; // Approve the product contract for items
      default:
        return null;
    }
  };

  const approvalAddress = getApprovalAddress();

  // Prepare approval transaction
  const { config: approveConfig, isSuccess: isApproveReady, error: approveError } = usePrepareContractWrite({
    address: ADDRESSES.TRINITY_TOKEN,
    abi: CONFIG.ERC20_ABI.abi as unknown as Abi,
    functionName: 'approve',
    args: approvalAddress && item?.price ? [approvalAddress, item.price] : undefined,
    enabled: !!approvalAddress && !!item?.price,
  });

  const { write: approve, data: approveData } = useContractWrite(approveConfig);

  const { isLoading: isApprovingTx, isSuccess: isApprovalSuccess = false } = useWaitForTransaction({
    hash: approveData?.hash,
  });

  // Get the correct ABI based on item type
  const getContractABI = () => {
    if (!item) return null;
    switch (item.type) {
      case 'entity':
        return CONFIG.TRINITY_ENTITY_ABI.abi as unknown as Abi;
      case 'product':
        return CONFIG.TRINITY_PRODUCT_ABI.abi as unknown as Abi;
      case 'item':
        return CONFIG.TRINITY_PRODUCT_ABI.abi as unknown as Abi; // Items are purchased through the product contract
      default:
        return null;
    }
  };

  // Get the correct contract address based on item type
  const getContractAddress = () => {
    if (!item) return null;
    switch (item.type) {
      case 'entity':
        return item.address;
      case 'product':
        return item.address;
      case 'item':
        return item.productAddress; // Use the product contract address for items
      default:
        return null;
    }
  };

  // Get the correct function name and args based on item type
  const getPurchaseConfig = () => {
    if (!item) return null;

    switch (item.type) {
      case 'entity':
        return {
          functionName: 'purchase' as const,
          args: [] as const,
        };
      case 'product':
        return {
          functionName: 'purchase' as const,
          args: selectedEntity ? [selectedEntity] as const : undefined,
        };
      case 'item':
        return {
          functionName: 'purchaseItem' as const,
          args: [item.id] as const,
        };
      default:
        return null;
    }
  };

  const purchaseConfig = getPurchaseConfig();
  const contractAddress = getContractAddress();
  const contractABI = getContractABI();

  // Prepare purchase transaction
  const { config: contractConfig, isSuccess: isPurchaseReady, error: purchaseError } = usePrepareContractWrite({
    address: contractAddress as `0x${string}`,
    abi: contractABI as Abi,
    functionName: purchaseConfig?.functionName || 'purchase',
    args: purchaseConfig?.args,
    enabled: Boolean(contractAddress && isApprovalSuccess && contractABI && purchaseConfig && 
      (item?.type !== 'product' || selectedEntity)),
  });

  const { write: purchase, data: purchaseData } = useContractWrite(contractConfig);

  const { isLoading: isPurchasingTx, isSuccess: isPurchaseSuccess = false } = useWaitForTransaction({
    hash: purchaseData?.hash,
  });

  // Handle approval completion and start purchase
  useEffect(() => {
    if (isApprovalSuccess && !isPurchasingTx && !isPurchaseSuccess && !isPurchaseInProgress) {
      console.log('Approval complete, preparing purchase...', {
        item,
        isPurchaseReady,
        purchaseError,
        abi: contractABI,
        purchaseConfig,
        contractAddress,
        approvalAddress
      });
      if (isPurchaseReady && purchase) {
        console.log('Purchase ready, executing...');
        setIsPurchaseInProgress(true);
        purchase();
      } else if (purchaseError) {
        console.error('Purchase preparation error:', purchaseError);
        onPurchaseError(new Error(purchaseError.message));
      }
    }
  }, [isApprovalSuccess, isPurchasingTx, isPurchaseSuccess, isPurchaseReady, purchase, purchaseError, onPurchaseError, item, purchaseConfig, contractAddress, approvalAddress, contractABI, isPurchaseInProgress]);

  // Handle purchase completion
  useEffect(() => {
    if (isPurchaseSuccess) {
      console.log('Purchase complete');
      setIsPurchaseInProgress(false);
      onPurchaseComplete();
    }
  }, [isPurchaseSuccess, onPurchaseComplete]);

  // Notify parent when approval is ready
  useEffect(() => {
    if (isApproveReady && approve) {
      onApprovalReady(async () => {
        try {
        if (!approve) {
            throw new Error('Approval function not ready');
          }
          await approve();
        } catch (error) {
          console.error('Approval error:', error);
          onApprovalError(error instanceof Error ? error : new Error('Approval failed'));
        }
      });
    } else if (approveError) {
      onApprovalError(new Error(approveError.message));
    }
  }, [isApproveReady, approve, approveError, onApprovalReady, onApprovalError]);

  return null;
}

export function Marketplace() {
  const { address } = useAccount()
  const { chain } = useNetwork()
  const publicClient = usePublicClient()
  const { data: walletClient } = useWalletClient()
  const { entities, isLoading } = useEntityDetails()
  const [purchaseError, setPurchaseError] = useState<string | null>(null)
  const [isPurchasing, setIsPurchasing] = useState(false)
  const [selectedItem, setSelectedItem] = useState<ForSaleItem | null>(null)
  const [selectedEntity, setSelectedEntity] = useState<`0x${string}` | null>(null)
  const [showEntitySelector, setShowEntitySelector] = useState(false)
  const [isApproving, setIsApproving] = useState(false)
  const [approvalItem, setApprovalItem] = useState<ForSaleItem | null>(null)
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)
  const [activeTab, setActiveTab] = useState<'all' | 'entities' | 'products' | 'items' | 'groups'>('all')

  // Get all items for sale
  const itemsForSale = useMemo(() => {
    if (!entities) {
      console.log('No entities found');
      return [];
    }
    
    console.log('Processing entities:', entities);
    const items: ForSaleItem[] = [];
    
    // Add entities for sale
    entities.forEach(entity => {
      console.log('Processing entity:', {
        name: entity.name,
        resell_value: entity.resell_value,
        products: entity.products?.length,
        owner: entity.owner
      });

      if (entity.resell_value > 0n) {
        items.push({
          type: 'entity',
          address: entity.address,
          name: entity.name,
          description: entity.description,
          price: entity.resell_value,
          owner: entity.owner
        });
      }

      // Add products for sale
      entity.products?.forEach(product => {
        console.log('Processing product:', {
          name: product.name,
          resell_value: product.resell_value,
          items: product.items?.length,
          owner: product.owner
        });

        if (product.resell_value > 0n) {
          items.push({
            type: 'product',
            address: product.address,
            name: product.name,
            description: product.description,
            price: product.resell_value,
            owner: product.owner,
            entityAddress: entity.address
          });
        }

        // Add ALL items from the product (without filtering by resell_value)
        product.items?.forEach(item => {
          console.log('Processing item:', {
            id: item.id,
            name: item.name,
            resellPrice: item.resellPrice?.toString(),
            owner: item.owner,
            address: item.address,
            ownerType: typeof item.owner
          });

          // Add all items regardless of resell value
          items.push({
            type: 'item',
            address: item.address,
            name: item.name,
            description: item.description,
            price: item.resellPrice || 0n, // Use resellPrice, default to 0 if undefined
            owner: item.owner,
            productAddress: product.address,
            id: item.id
          });
        });
      });
    });

    console.log('Final items for sale:', items.map(item => ({
      type: item.type,
      name: item.name,
      price: item.price.toString(),
      owner: item.owner
    })));
    return items;
  }, [entities]);

  // Filter items based on active tab
  const filteredItems = useMemo(() => {
    console.log('Filtering items for tab:', activeTab);
    console.log('Items before filtering:', itemsForSale);
    
    const filtered = itemsForSale.filter(item => {
      if (activeTab === 'all') return true;
      if (activeTab === 'groups') return true;
      // Map tab names to item types
      const typeMap: Record<string, ForSaleItem['type']> = {
        'entities': 'entity',
        'products': 'product',
        'items': 'item'
      };
      return item.type === typeMap[activeTab];
    });

    console.log('Filtered items:', filtered);
    return filtered;
  }, [itemsForSale, activeTab]);

  // Group items by type
  const groupedItems = useMemo(() => {
    console.log('Grouping items');
    console.log('Items before grouping:', itemsForSale);
    
    const grouped = {
      entities: itemsForSale.filter(item => item.type === 'entity'),
      products: itemsForSale.filter(item => item.type === 'product'),
      items: itemsForSale.filter(item => item.type === 'item')
    };

    console.log('Grouped items:', grouped);
    return grouped;
  }, [itemsForSale]);

  // Get user's balance
  const { data: balance } = useBalance({
    address: address,
    token: ADDRESSES.TRINITY_TOKEN,
  });

  // Get all allowances at once
  const { data: entityAllowances } = useAllowance({
    token: ADDRESSES.TRINITY_TOKEN,
    spender: itemsForSale[0]?.address,
  });

  const { data: productAllowances } = useAllowance({
    token: ADDRESSES.TRINITY_TOKEN,
    spender: itemsForSale.find(item => item.type === 'product')?.address,
  });

  const handleApprovalReady = useCallback((approve: () => Promise<void>) => {
    console.log('Approval ready, proceeding with transaction');
    approve();
  }, []);

  const handleApprovalError = useCallback((error: Error) => {
    console.error('Approval error:', error);
    setPurchaseError(error.message);
    setIsPurchasing(false);
    setSelectedItem(null);
    setSelectedEntity(null);
  }, []);

  const handlePurchaseComplete = useCallback(() => {
    console.log('Purchase complete, resetting state');
    setIsPurchasing(false);
    setSelectedItem(null);
    setSelectedEntity(null);
    setShowSuccessDialog(true);
  }, []);

  const handlePurchaseError = useCallback((error: Error) => {
    console.error('Purchase error:', error);
    setPurchaseError(error.message);
    setIsPurchasing(false);
    setSelectedItem(null);
    setSelectedEntity(null);
  }, []);

  // Handle entity selection from modal
  const handleEntitySelect = (entityAddress: string) => {
    console.log('Entity selected:', entityAddress);
    setSelectedEntity(entityAddress as `0x${string}`);
    setShowEntitySelector(false);
  };

  // Effect to handle entity purchase
  useEffect(() => {
    const handleEntityPurchase = async () => {
      if (!selectedItem || selectedItem.type !== 'entity' || !selectedItem.price || !walletClient || !address) return;

      try {
        // Check if we need approval
        const currentAllowance = await publicClient.readContract({
          address: ADDRESSES.TRINITY_TOKEN,
          abi: CONFIG.ERC20_ABI.abi as unknown as Abi,
          functionName: 'allowance',
          args: [address, selectedItem.address],
        }) as bigint;

        if (currentAllowance < selectedItem.price) {
          console.log('Need approval');
          setIsApproving(true);
          return;
        }

        // Execute purchase
        console.log('Executing purchase...');
        const { request: purchaseRequest } = await publicClient.simulateContract({
          address: selectedItem.address,
          abi: CONFIG.TRINITY_ENTITY_ABI.abi as unknown as Abi,
          functionName: 'purchase',
          args: [],
          account: address
        });

        console.log('Purchase request:', purchaseRequest);

        const purchaseHash = await walletClient.writeContract(purchaseRequest);
        console.log('Purchase transaction hash:', purchaseHash);

        // Wait for purchase transaction to be mined
        const purchaseReceipt = await publicClient.waitForTransactionReceipt({ hash: purchaseHash });
        console.log('Purchase transaction mined:', purchaseReceipt);

        // Show success dialog after transaction is confirmed
        toast.success('Entity purchased successfully');
        setShowSuccessDialog(true);
        setSelectedItem(null);
        setIsApproving(false);
      } catch (error) {
        console.error('Error purchasing entity:', error);
        toast.error('Failed to purchase entity');
        setSelectedItem(null);
        setIsApproving(false);
      }
    };

    if (selectedItem?.type === 'entity' && !isApproving) {
      handleEntityPurchase();
    }
  }, [selectedItem, isApproving, publicClient, walletClient, address]);

  // Effect to handle product purchase after entity selection
  useEffect(() => {
    const handleProductPurchase = async () => {
      if (!selectedItem || selectedItem.type !== 'product' || !selectedItem.price || !walletClient || !selectedEntity || !address) {
        console.log('Product purchase conditions not met:', {
          hasSelectedItem: !!selectedItem,
          itemType: selectedItem?.type,
          hasPrice: !!selectedItem?.price,
          hasWalletClient: !!walletClient,
          hasSelectedEntity: !!selectedEntity,
          hasAddress: !!address
        });
        return;
      }

      try {
        console.log('Starting product purchase flow:', {
          product: selectedItem,
          entity: selectedEntity,
          wallet: address
        });

        // Check if we need approval
        const currentAllowance = await publicClient.readContract({
          address: ADDRESSES.TRINITY_TOKEN,
          abi: CONFIG.ERC20_ABI.abi as unknown as Abi,
          functionName: 'allowance',
          args: [address, selectedItem.address],
        }) as bigint;

        console.log('Current allowance:', {
          allowance: currentAllowance.toString(),
          price: selectedItem.price.toString()
        });

        if (currentAllowance < selectedItem.price) {
          console.log('Need approval');
          setIsApproving(true);
          return;
        }

        // Execute purchase
        console.log('Executing purchase...');
        const { request: purchaseRequest } = await publicClient.simulateContract({
          address: selectedItem.address,
          abi: CONFIG.TRINITY_PRODUCT_ABI.abi as unknown as Abi,
          functionName: 'purchase',
          args: [selectedEntity],
          account: address
        });

        console.log('Purchase request:', purchaseRequest);

        const purchaseHash = await walletClient.writeContract(purchaseRequest);
        console.log('Purchase transaction hash:', purchaseHash);

        // Wait for purchase transaction to be mined
        const purchaseReceipt = await publicClient.waitForTransactionReceipt({ hash: purchaseHash });
        console.log('Purchase transaction mined:', purchaseReceipt);

        // Show success dialog after transaction is confirmed
        toast.success('Product purchased successfully');
        setShowSuccessDialog(true);
        setSelectedItem(null);
        setSelectedEntity(null);
        setIsApproving(false);
      } catch (error) {
        console.error('Error purchasing product:', error);
        toast.error('Failed to purchase product');
        setSelectedItem(null);
        setSelectedEntity(null);
        setIsApproving(false);
      }
    };

    if (selectedItem?.type === 'product' && !isApproving && selectedEntity) {
      console.log('Triggering product purchase effect:', {
        selectedItem,
        selectedEntity,
        isApproving
      });
      handleProductPurchase();
    }
  }, [selectedItem, selectedEntity, isApproving, publicClient, walletClient, address]);

  // Debug entities data
  useEffect(() => {
    console.log('Raw entities data:', entities);
    console.log('Entity details:', entities?.map(e => ({
      name: e.name,
      address: e.address,
      resell_value: e.resell_value,
      owner: e.owner,
      products: e.products?.map(p => ({
        name: p.name,
        address: p.address,
        resell_value: p.resell_value,
        owner: p.owner
      }))
    })));
  }, [entities]);

  console.log('Final itemsForSale:', itemsForSale);

  if (!address) {
    return (
      <div className="text-center py-10">
        Please connect your wallet to view the Marketplace.
      </div>
    )
  }

  if (!entities) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  console.log('itemsForSale', itemsForSale);
  if (itemsForSale.length === 0) {
    return (
      <div className="min-h-screen p-8">
      <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
            <h1 className="text-4xl font-bold mb-3 bg-gradient-to-r from-blue-500/90 via-purple-500/90 to-pink-500/90 text-transparent bg-clip-text">For Sale</h1>
            </div>
          </div>
          <div className="flex flex-col items-center justify-center min-h-[10vh]">
            <h2 className="text-2xl font-bold text-white mb-2">Community Products for Sale</h2>
          </div>
        </div>
      </div>
      )
  }

  return (
    <div className="min-h-screen">
    <div className="max-w-6xl mx-auto p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-0 bg-gradient-to-r from-blue-500/90 via-purple-500/90 to-pink-500/90 text-transparent bg-clip-text">Marketplace</h1>
            <p className="text-base text-gray-400/40">Community Products for Sale</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <div className="text-secondary text-sm">Your Balance</div>
              <div className="text-primary font-bold">
                {balance ? formatTokenAmount(balance.value) : '0'} †
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          <div className="flex justify-between items-center">
            <div className="flex space-x-2">
              <button
                onClick={() => setActiveTab('all')}
                className={`px-4 py-2 rounded-lg ${
                  activeTab === 'all'
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
                    : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setActiveTab('groups')}
                className={`px-4 py-2 rounded-lg ${
                  activeTab === 'groups'
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
                    : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
              >
                Groups
              </button>
              <button
                onClick={() => setActiveTab('entities')}
                className={`px-4 py-2 rounded-lg ${
                  activeTab === 'entities'
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
                    : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
              >
                Entities
              </button>
              <button
                onClick={() => setActiveTab('products')}
                className={`px-4 py-2 rounded-lg ${
                  activeTab === 'products'
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
                    : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
              >
                Products
              </button>
            <button 
                onClick={() => setActiveTab('items')}
                className={`px-4 py-2 rounded-lg ${
                  activeTab === 'items'
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
                    : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
              >
                Items
            </button>
            </div>
          </div>

          <div className="space-y-8">
            {activeTab === 'groups' ? (
              <>
                {groupedItems.entities.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold">Entities</h3>
                    {groupedItems.entities.map((item) => {
                      console.log('Rendering entity item:', {
                        item,
                        address,
                        owner: item.owner,
                        ownerType: typeof item.owner
                      });
                      const isOwnedByUser = address && item.owner && 
                        typeof item.owner === 'string' && 
                        address.toLowerCase() === item.owner.toLowerCase();
                      const hasEnoughBalance = balance && BigInt(balance.value) >= item.price;
                      const hasEnoughAllowance = entityAllowances ? BigInt(entityAllowances) >= item.price : false;
                      const canPurchase = !isOwnedByUser && hasEnoughBalance && hasEnoughAllowance;
                      const needsApproval = !isOwnedByUser && hasEnoughBalance && !hasEnoughAllowance;
              
              return (
                        <MarketplaceItem
                          key={`${item.type}-${item.address}`}
                          item={item}
                          isOwnedByUser={isOwnedByUser}
                          canPurchase={canPurchase}
                          needsApproval={needsApproval}
                          onPurchase={() => setSelectedItem(item)}
                          onApprove={() => {
                            setSelectedItem(item);
                            setIsApproving(true);
                          }}
                        />
                      );
                    })}
                    </div>
                )}

                {groupedItems.products.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold">Products</h3>
                    {groupedItems.products.map((item) => {
                      console.log('Rendering product item:', {
                        item,
                        address,
                        owner: item.owner,
                        ownerType: typeof item.owner
                      });
                      const isOwnedByUser = address && item.owner && 
                        typeof item.owner === 'string' && 
                        address.toLowerCase() === item.owner.toLowerCase();
                      const hasEnoughBalance = balance && BigInt(balance.value) >= item.price;
                      const hasEnoughAllowance = productAllowances ? BigInt(productAllowances) >= item.price : false;
                      const canPurchase = !isOwnedByUser && hasEnoughBalance && hasEnoughAllowance;
                      const needsApproval = !isOwnedByUser && hasEnoughBalance && !hasEnoughAllowance;

                      return (
                        <MarketplaceItem
                          key={`${item.type}-${item.address}`}
                          item={item}
                          isOwnedByUser={isOwnedByUser}
                          canPurchase={canPurchase}
                          needsApproval={needsApproval}
                          onPurchase={() => setSelectedItem(item)}
                          onApprove={() => {
                            setSelectedItem(item);
                            setIsApproving(true);
                          }}
                        />
                      );
                    })}
                      </div>
                )}

                {groupedItems.items.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold">Items</h3>
                    {groupedItems.items.map((item) => {
                      console.log('Rendering item:', {
                        item,
                        address,
                        owner: item.owner,
                        ownerType: typeof item.owner
                      });
                      const isOwnedByUser = address && item.owner && 
                        typeof item.owner === 'string' && 
                        address.toLowerCase() === item.owner.toLowerCase();
                      const hasEnoughBalance = balance && BigInt(balance.value) >= item.price;
                      const hasEnoughAllowance = productAllowances ? BigInt(productAllowances) >= item.price : false;
                      const canPurchase = !isOwnedByUser && hasEnoughBalance && hasEnoughAllowance;
                      const needsApproval = !isOwnedByUser && hasEnoughBalance && !hasEnoughAllowance;

                      return (
                        <MarketplaceItem
                          key={`${item.type}-${item.address}-${item.id}`}
                          item={item}
                          isOwnedByUser={isOwnedByUser}
                          canPurchase={canPurchase}
                          needsApproval={needsApproval}
                          onPurchase={() => setSelectedItem(item)}
                          onApprove={() => {
                            setSelectedItem(item);
                            setIsApproving(true);
                          }}
                        />
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-4">
                {filteredItems.map((item) => {
                  console.log('Rendering filtered item:', {
                    item,
                    address,
                    owner: item.owner,
                    ownerType: typeof item.owner
                  });
                  const isOwnedByUser = address && item.owner && 
                    typeof item.owner === 'string' && 
                    address.toLowerCase() === item.owner.toLowerCase();
                  const hasEnoughBalance = balance && BigInt(balance.value) >= item.price;
                  const hasEnoughAllowance = entityAllowances ? BigInt(entityAllowances) >= item.price : false;
                  const canPurchase = !isOwnedByUser && hasEnoughBalance && hasEnoughAllowance;
                  const needsApproval = !isOwnedByUser && hasEnoughBalance && !hasEnoughAllowance;

                  return (
                    <MarketplaceItem
                      key={`${item.type}-${item.address}-${item.id || ''}`}
                      item={item}
                      isOwnedByUser={isOwnedByUser}
                      canPurchase={canPurchase}
                      needsApproval={needsApproval}
                      onPurchase={() => {
                        console.log('Purchase clicked:', item);
                        setSelectedItem(item);
                        if (item.type === 'product') {
                          console.log('Opening entity selector for product purchase');
                          setShowEntitySelector(true);
                        }
                      }}
                      onApprove={() => {
                        console.log('Approval clicked:', item);
                        setSelectedItem(item);
                        setIsApproving(true);
                      }}
                    />
                  );
            })}
          </div>
        )}
          </div>
        </div>

        {/* Entity Selection Modal */}
        <EntitySelectionModal
          isOpen={showEntitySelector}
          onClose={() => {
            console.log('Entity selector closed');
            setShowEntitySelector(false);
            if (!selectedEntity) {
              setSelectedItem(null);
            }
          }}
          onSelect={handleEntitySelect}
          productName={selectedItem?.name || ''}
        />

        {/* Approval Handler */}
        <ApprovalHandler
          item={selectedItem}
          selectedEntity={selectedEntity}
          onApprovalReady={handleApprovalReady}
          onApprovalError={handleApprovalError}
          onPurchaseComplete={handlePurchaseComplete}
          onPurchaseError={handlePurchaseError}
        />

        {/* Success Dialog */}
        <SuccessDialog
          isOpen={showSuccessDialog}
          onClose={() => {
            setShowSuccessDialog(false);
            setSelectedItem(null);
            setSelectedEntity(null);
            setIsApproving(false);
          }}
          item={selectedItem}
        />
      </div>
    </div>
  )
} 